import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, Wand2, Save, Printer, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Header } from '../components/Header';
import { OnePagerPreview } from '../components/OnePagerPreview';
import { PreviewScaler } from '../components/PreviewScaler';
import { templates } from '../data/templates';
import { colorSchemes, printSizes } from '../data/catalog';
import { printPreview } from '../lib/print';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const examples = [
  'A hand hygiene one-pager for nurses using the WHO 5 Moments',
  'A patient handout on fall prevention after surgery',
  'A de-escalation techniques quick reference for behavioral health staff',
  'A checklist for PPE donning and doffing',
];

export default function AIAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const [prompt, setPrompt] = useState('');
  const [layout, setLayout] = useState('standard');
  const [audience, setAudience] = useState('staff');
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [colorScheme] = useState(colorSchemes[0]);
  const [printSize, setPrintSize] = useState('letter');
  const [docId, setDocId] = useState(null);
  const [saving, setSaving] = useState(false);

  const buildOnePager = () => ({ id: docId, title: content?.title, subtitle: content?.subtitle, content: content || {}, colorScheme, layout, docType: 'one-pager' });

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const res = await api.post('/generate-onepager', { topic: prompt, category: 'General', layout, targetAudience: audience, organization: '' });
      setContent(res.data.data);
    } catch { toast({ title: 'Generation failed', variant: 'destructive' }); }
    finally { setGenerating(false); }
  };

  const handleSave = async () => {
    if (!user) { navigate('/login'); return; }
    setSaving(true);
    try { const payload = buildOnePager(); const res = docId ? await api.put(`/onepagers/${docId}`, payload) : await api.post('/onepagers', payload); setDocId(res.data.id); toast({ title: 'Saved to your library!' }); }
    catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full text-purple-700 text-sm font-medium mb-3"><Sparkles className="w-4 h-4" /> AI Assistant</div>
          <h1 className="text-3xl font-bold text-gray-900">Describe what you need</h1>
          <p className="text-gray-500 mt-1">Tell the AI in plain language and it drafts the training material.</p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="mb-4"><CardContent className="p-4">
            <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Create a one-pager about hand hygiene for nurses..." className="mb-3 resize-none" />
            <div className="flex flex-wrap gap-2 mb-3">
              <select value={layout} onChange={(e) => setLayout(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">{templates.map((t) => <option key={t.id} value={t.layout}>{t.name}</option>)}</select>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="border rounded-lg px-3 py-2 text-sm"><option value="staff">Staff Training</option><option value="patient">Patient Education</option></select>
              <Button onClick={generate} disabled={generating || !prompt.trim()} className="ml-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white">{generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Drafting...</> : <><Send className="w-4 h-4 mr-1" /> Generate</>}</Button>
            </div>
            {!content && (
              <div className="flex flex-wrap gap-2">{examples.map((ex, i) => <button key={i} onClick={() => setPrompt(ex)} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-purple-50 text-gray-600 hover:text-purple-700 transition-colors">{ex}</button>)}</div>
            )}
          </CardContent></Card>
        </div>

        {content && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-[1fr_300px] gap-6 mt-4">
            <div className="border rounded-xl bg-gray-100 p-4 overflow-hidden"><div ref={previewRef}><PreviewScaler><OnePagerPreview onePager={buildOnePager()} /></PreviewScaler></div></div>
            <div className="space-y-3">
              <Button onClick={generate} variant="outline" className="w-full"><Wand2 className="w-4 h-4 mr-1" /> Regenerate</Button>
              <Button onClick={() => printPreview(previewRef.current, `print-size-${printSize}`)} variant="outline" className="w-full"><Printer className="w-4 h-4 mr-1" /> Print</Button>
              <Button onClick={handleSave} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save to Library</Button>
              <select value={printSize} onChange={(e) => setPrintSize(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">{printSizes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}
